import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";

export const THREADS_KEY = ["chat", "threads"];
export const messagesKey = (tid) => ["chat", "messages", tid];

/** ───── API ───── */
export const chatApi = {
  listThreads: () => api.get("/chat/threads").then((r) => r.data),

  createThread: async ({ other_user_id }) => {
    const r = await api.post(
      "/chat/threads",
      { other_user_id },
      { validateStatus: (s) => s < 500 }
    );
    if (r.status === 403) {
      const err = new Error("not_a_match");
      err.code = "not_a_match";
      err.status = 403;
      throw err;
    }
    if (r.status >= 400) {
      const err = new Error(
        formatApiErrorDetail(r.data?.detail) || "Failed to open thread"
      );
      err.status = r.status;
      throw err;
    }
    return r.data;
  },

  listMessages: (tid, since) =>
    api
      .get(`/chat/threads/${tid}/messages${since ? `?since=${since}` : ""}`)
      .then((r) => r.data),

  sendMessage: ({ tid, text }) =>
    api.post(`/chat/threads/${tid}/messages`, { text }).then((r) => r.data),

  markRead: (tid) =>
    api.post(`/chat/threads/${tid}/read`).then((r) => r.data),
};

/** ───── Hooks ───── */
export function useThreads({ poll = false } = {}) {
  return useQuery({
    queryKey: THREADS_KEY,
    queryFn: chatApi.listThreads,
    refetchInterval: poll ? 15_000 : false,
    refetchIntervalInBackground: false,
  });
}

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: chatApi.createThread,
    onSuccess: () => qc.invalidateQueries({ queryKey: THREADS_KEY }),
  });
}

export function useSendMessage(tid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text) => chatApi.sendMessage({ tid, text }),
    onSuccess: (msg) => {
      qc.setQueryData(messagesKey(tid), (old) => {
        if (!old) return [msg];
        if (old.some((m) => m.message_id === msg.message_id)) return old;
        return [...old, msg];
      });
      qc.invalidateQueries({ queryKey: THREADS_KEY });
    },
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: chatApi.markRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: THREADS_KEY }),
  });
}

/**
 * useMessages — initial fetch + 3 s polling (only while document visible).
 * Auto-marks-read on any successful poll that returns new messages.
 */
export function useMessages(tid, enabled = true) {
  const qc = useQueryClient();
  const lastIdRef = useRef(null);

  const query = useQuery({
    queryKey: messagesKey(tid),
    queryFn: async () => {
      const data = await chatApi.listMessages(tid);
      lastIdRef.current = data.length ? data[data.length - 1].message_id : null;
      // Mark anything we just received from the other side as read
      try {
        await chatApi.markRead(tid);
        qc.invalidateQueries({ queryKey: THREADS_KEY });
      } catch {
        /* noop */
      }
      return data;
    },
    enabled: enabled && !!tid,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (!enabled || !tid) return;
    let cancelled = false;

    async function poll() {
      if (document.visibilityState !== "visible") return;
      const since = lastIdRef.current;
      if (!since) return;
      try {
        const newMsgs = await chatApi.listMessages(tid, since);
        if (cancelled || newMsgs.length === 0) return;
        qc.setQueryData(messagesKey(tid), (old) => {
          const seen = new Set((old || []).map((m) => m.message_id));
          const additions = newMsgs.filter((m) => !seen.has(m.message_id));
          if (additions.length === 0) return old;
          const merged = [...(old || []), ...additions];
          lastIdRef.current = merged[merged.length - 1].message_id;
          // mark read + refresh thread badges
          chatApi.markRead(tid).catch(() => {});
          qc.invalidateQueries({ queryKey: THREADS_KEY });
          return merged;
        });
      } catch {
        /* poll failures swallowed */
      }
    }

    const id = setInterval(poll, 3000);
    const onVis = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [tid, qc, enabled]);

  return query;
}

/** ───── Formatting ───── */
export function formatThreadTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 2) return "yesterday";
  if (day < 7) return `${day}d`;
  return d.toLocaleDateString();
}
